import React, { useState } from 'react';
import TicketForm from '../components/TicketForm';
import TriageResult from '../components/TriageResult';
import EvidenceChecklist from '../components/EvidenceChecklist';
import EscalationSummary from '../components/EscalationSummary';
import { createTicket, getEvidencePlan, submitEvidence } from '../utils/api';
import { saveOfflineTicket, offlineTriage, saveFieldRequest, updateFieldRequestStatus, saveOfflineEvidence } from '../utils/offlineCache';

/**
 * Field UI — Mobile technician interface.
 * Walks through: Submit Ticket → Triage → Evidence → Escalation
 */

// Workflow steps
const STEPS = {
  SUBMIT: 'submit',
  TRIAGE: 'triage',
  EVIDENCE: 'evidence',
  ESCALATION: 'escalation',
  WAITING: 'waiting',
};

function FieldUI({ isOnline }) {
  const [currentStep, setCurrentStep] = useState(STEPS.SUBMIT);
  const [ticket, setTicket] = useState(null);
  const [triageResult, setTriageResult] = useState(null);
  const [evidencePlan, setEvidencePlan] = useState(null);
  const [escalation, setEscalation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // --- Step 1: Submit Ticket ---
  const handleSubmitTicket = async (formData) => {
    setLoading(true);
    setError(null);

    try {
      if (isOnline) {
        const response = await createTicket(formData);
        setTicket(response.ticket);
        setTriageResult(response.triage);
        await saveFieldRequest({
          ticket_id: response.ticket.ticket_id,
          fault_code: formData.fault_code,
          equipment_model: formData.equipment_model,
          equipment_type: formData.equipment_type,
          model_year: formData.model_year,
          operating_hours: formData.operating_hours,
          symptoms: formData.symptoms,
          status: 'triaged',
        });
      } else {
        const offlineTicket = await saveOfflineTicket(formData);
        const offlineTr = offlineTriage(formData.fault_code, formData.symptoms);
        setTicket({ ...offlineTicket, ...formData });
        setTriageResult({ ...offlineTr, offline: true });
        await saveFieldRequest({
          ticket_id: offlineTicket.ticket_id,
          fault_code: formData.fault_code,
          equipment_model: formData.equipment_model,
          equipment_type: formData.equipment_type,
          model_year: formData.model_year,
          operating_hours: formData.operating_hours,
          symptoms: formData.symptoms,
          status: 'pending_sync',
          offline_created: true,
        });
      }
      setCurrentStep(STEPS.TRIAGE);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // --- Step 2: Proceed to Evidence Collection ---
  const handleProceedToEvidence = async () => {
    setLoading(true);
    setError(null);

    try {
      if (isOnline && ticket?.ticket_id) {
        const response = await getEvidencePlan(ticket.ticket_id);
        setEvidencePlan(response.plan);
      } else {
        setEvidencePlan(null);
      }
      if (ticket?.ticket_id) {
        await updateFieldRequestStatus(ticket.ticket_id, 'evidence_collecting');
      }
      setCurrentStep(STEPS.EVIDENCE);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // --- Step 3: Submit Evidence ---
  const handleSubmitEvidence = async (completedSteps) => {
    setLoading(true);
    setError(null);

    try {
      if (isOnline && ticket?.ticket_id) {
        const response = await submitEvidence(ticket.ticket_id, completedSteps);
        setEscalation(response.escalation);
        await updateFieldRequestStatus(
          ticket.ticket_id,
          response.requires_approval ? 'escalation_pending' : 'completed'
        );
        if (response.requires_approval) {
          setCurrentStep(STEPS.ESCALATION);
        } else {
          setCurrentStep(STEPS.WAITING);
        }
      } else {
        if (ticket?.ticket_id) {
          await saveOfflineEvidence(ticket.ticket_id, completedSteps);
          await updateFieldRequestStatus(ticket.ticket_id, 'evidence_submitted');
        }
        setEscalation({
          escalation_summary: 'Evidence saved locally. The full evaluation will run automatically when connectivity is restored.',
          recommended_action: 'Evidence has been saved on this device. Once you are back online, the system will submit it and generate the escalation summary.',
          urgency: 'ROUTINE',
          confidence: 0,
        });
        setCurrentStep(STEPS.WAITING);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // --- Reset to start new ticket ---
  const handleNewTicket = () => {
    setCurrentStep(STEPS.SUBMIT);
    setTicket(null);
    setTriageResult(null);
    setEvidencePlan(null);
    setEscalation(null);
    setError(null);
  };

  return (
    <div className="page-content page-content--mobile">
      {/* Progress indicator */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24 }}>
        {Object.values(STEPS).map((step, i) => (
          <div
            key={step}
            style={{
              flex: 1,
              height: 3,
              borderRadius: 2,
              background:
                Object.values(STEPS).indexOf(currentStep) >= i
                  ? 'var(--color-accent)'
                  : 'var(--border-color)',
              transition: 'background 300ms ease',
            }}
          />
        ))}
      </div>

      {/* Error display */}
      {error && (
        <div style={{
          background: 'var(--color-danger-bg)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: 'var(--radius-md)',
          padding: 'var(--space-md)',
          marginBottom: 'var(--space-md)',
          color: 'var(--color-danger)',
          fontSize: 13,
        }}>
          {error}
        </div>
      )}

      {/* Step 1: Ticket Submission */}
      {currentStep === STEPS.SUBMIT && (
        <TicketForm onSubmit={handleSubmitTicket} loading={loading} />
      )}

      {/* Step 2: Triage Result */}
      {currentStep === STEPS.TRIAGE && triageResult && (
        <TriageResult
          result={triageResult}
          onProceed={handleProceedToEvidence}
          loading={loading}
          faultCode={ticket?.fault_code}
        />
      )}

      {/* Step 3: Evidence Collection */}
      {currentStep === STEPS.EVIDENCE && (
        <EvidenceChecklist
          plan={evidencePlan}
          ticket={ticket}
          triageResult={triageResult}
          isOnline={isOnline}
          onSubmit={handleSubmitEvidence}
          loading={loading}
        />
      )}

      {/* Step 4: Escalation / Waiting */}
      {(currentStep === STEPS.ESCALATION || currentStep === STEPS.WAITING) && (
        <EscalationSummary
          escalation={escalation}
          ticket={ticket}
          onNewTicket={handleNewTicket}
        />
      )}

      {/* Emergency Escalate — always visible except on submit screen */}
      {currentStep !== STEPS.SUBMIT && (
        <button
          className="btn btn--emergency btn--block"
          style={{ marginTop: 24 }}
          onClick={() => {
            alert('Emergency escalation sent to supervisor. Stay safe.');
            handleNewTicket();
          }}
        >
          ⚠ Emergency Escalate
        </button>
      )}
    </div>
  );
}

export default FieldUI;
